/**
 * NavigatorX Create User Modal
 *
 * Admin-initiated user creation with full plan control:
 * - Basic profile (name, email, phone, company)
 * - Plan type: None, Trial, Free Months, Subscription
 * - Custom duration per plan type
 *
 * Writes to nav_users via navigatorXService.createUser().
 * Checks for duplicate emails before creation.
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  X,
  Loader2,
  UserPlus,
  Mail,
  Phone,
  Building2,
  AlertCircle,
  Clock,
  Crown,
  Gift,
  UserX
} from 'lucide-react';
import { toast } from 'sonner';
import { createUser, getUserByEmail } from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';
import type { NavUser, NavUserCreateInput } from '../../../types/navigatorx';

// ============================================================================
// TYPES
// ============================================================================

interface CreateUserModalProps {
  onClose: () => void;
  onCreated: (newUser: NavUser) => void;
}

type PlanType = 'none' | 'trial' | 'freeMonths' | 'subscription';

interface PlanOption {
  value: PlanType;
  label: string;
  description: string;
  icon: typeof Clock;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PLAN_OPTIONS: PlanOption[] = [
  { value: 'none', label: 'None', description: 'Account only, no active plan', icon: UserX },
  { value: 'trial', label: 'Trial', description: 'Time-limited trial period', icon: Clock },
  { value: 'freeMonths', label: 'Free Months', description: 'Granted free months', icon: Gift },
  { value: 'subscription', label: 'Subscription', description: 'Active paid subscription', icon: Crown },
];

const TRIAL_PRESETS = [7, 14, 30, 60, 90];
const MONTH_PRESETS = [1, 3, 6, 12];

// ============================================================================
// COMPONENT
// ============================================================================

export default function CreateUserModal({ onClose, onCreated }: CreateUserModalProps) {
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    planType: 'none' as PlanType,
    trialDays: 14,
    subscriptionMonths: 1,
    freeMonths: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // --------------------------------------------------------------------------
  // VALIDATION
  // --------------------------------------------------------------------------

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone) {
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        newErrors.phone = 'Phone number must be at least 10 digits';
      }
    }

    if (formData.planType === 'trial') {
      if (!formData.trialDays || formData.trialDays < 1) {
        newErrors.trialDays = 'Trial must be at least 1 day';
      } else if (formData.trialDays > 365) {
        newErrors.trialDays = 'Trial cannot exceed 365 days';
      }
    }

    if (formData.planType === 'subscription') {
      if (!formData.subscriptionMonths || formData.subscriptionMonths < 1) {
        newErrors.subscriptionMonths = 'Must be at least 1 month';
      } else if (formData.subscriptionMonths > 60) {
        newErrors.subscriptionMonths = 'Cannot exceed 60 months';
      }
    }

    if (formData.planType === 'freeMonths') {
      if (!formData.freeMonths || formData.freeMonths < 1) {
        newErrors.freeMonths = 'Must be at least 1 month';
      } else if (formData.freeMonths > 24) {
        newErrors.freeMonths = 'Cannot exceed 24 months';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before creating');
      return;
    }

    setCreating(true);

    try {
      const adminId = auth.currentUser?.uid;
      if (!adminId) {
        toast.error('You must be logged in to create users');
        setCreating(false);
        return;
      }

      // Check for duplicate email
      const existingUser = await getUserByEmail(formData.email.trim().toLowerCase());
      if (existingUser) {
        setErrors({ email: 'A user with this email already exists' });
        setCreating(false);
        return;
      }

      const input: NavUserCreateInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || undefined,
        company: formData.company.trim() || undefined,
      };

      switch (formData.planType) {
        case 'trial':
          input.trialDays = formData.trialDays;
          break;
        case 'subscription':
          input.subscriptionMonths = formData.subscriptionMonths;
          break;
        case 'freeMonths':
          input.freeMonths = formData.freeMonths;
          break;
        case 'none':
          input.subscriptionStatus = 'NONE';
          break;
      }

      const newUser = await createUser(input, adminId);
      toast.success(`Created ${formData.firstName} ${formData.lastName}`);
      onCreated(newUser);
      onClose();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // --------------------------------------------------------------------------
  // SUMMARY TEXT
  // --------------------------------------------------------------------------

  const getSummaryText = (): string => {
    const name = formData.firstName.trim() && formData.lastName.trim()
      ? `${formData.firstName.trim()} ${formData.lastName.trim()}`
      : 'new user';

    switch (formData.planType) {
      case 'trial':
        return `Create ${name} with a ${formData.trialDays}-day trial`;
      case 'subscription':
        return `Create ${name} with a ${formData.subscriptionMonths}-month subscription`;
      case 'freeMonths':
        return `Create ${name} with ${formData.freeMonths} free month${formData.freeMonths > 1 ? 's' : ''}`;
      case 'none':
        return `Create ${name} with no active plan`;
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserPlus className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">Create User</h2>
              <p className="text-amber-100 text-sm">Add a new NavigatorX user</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 p-1 rounded"
            disabled={creating}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="createFirstName" className="flex items-center gap-1">
                First Name
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="createFirstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-red-500' : ''}
                disabled={creating}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="createLastName" className="flex items-center gap-1">
                Last Name
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="createLastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-red-500' : ''}
                disabled={creating}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="createEmail" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              Email Address
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="createEmail"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-red-500' : ''}
              disabled={creating}
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="createPhone" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              Phone Number
            </Label>
            <Input
              id="createPhone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              className={errors.phone ? 'border-red-500' : ''}
              disabled={creating}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Company */}
          <div>
            <Label htmlFor="createCompany" className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              Company
            </Label>
            <Input
              id="createCompany"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Optional"
              disabled={creating}
            />
          </div>

          {/* Divider */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <Crown className="h-4 w-4 text-gray-400" />
              Plan Type
            </Label>

            {/* Plan Type Selector */}
            <div className="grid grid-cols-2 gap-2">
              {PLAN_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = formData.planType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('planType', option.value)}
                    disabled={creating}
                    className={`p-3 border rounded-lg text-left transition-all ${
                      isSelected
                        ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    } ${creating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-amber-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-amber-900' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                    </div>
                    <p className={`text-xs ${isSelected ? 'text-amber-700' : 'text-gray-500'}`}>
                      {option.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration Controls — conditional based on plan type */}
          {formData.planType === 'trial' && (
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-gray-400" />
                Trial Duration (days)
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {TRIAL_PRESETS.map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => handleInputChange('trialDays', days)}
                    disabled={creating}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      formData.trialDays === days
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                max={365}
                value={formData.trialDays}
                onChange={(e) => handleInputChange('trialDays', parseInt(e.target.value) || 0)}
                className={errors.trialDays ? 'border-red-500' : ''}
                disabled={creating}
              />
              {errors.trialDays && (
                <p className="text-red-500 text-xs mt-1">{errors.trialDays}</p>
              )}
            </div>
          )}

          {formData.planType === 'subscription' && (
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4 text-gray-400" />
                Subscription Duration (months)
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {MONTH_PRESETS.map(months => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => handleInputChange('subscriptionMonths', months)}
                    disabled={creating}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      formData.subscriptionMonths === months
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    {months}mo
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                max={60}
                value={formData.subscriptionMonths}
                onChange={(e) => handleInputChange('subscriptionMonths', parseInt(e.target.value) || 0)}
                className={errors.subscriptionMonths ? 'border-red-500' : ''}
                disabled={creating}
              />
              {errors.subscriptionMonths && (
                <p className="text-red-500 text-xs mt-1">{errors.subscriptionMonths}</p>
              )}
            </div>
          )}

          {formData.planType === 'freeMonths' && (
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Gift className="h-4 w-4 text-gray-400" />
                Free Months
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {MONTH_PRESETS.map(months => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => handleInputChange('freeMonths', months)}
                    disabled={creating}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      formData.freeMonths === months
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    {months}mo
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                max={24}
                value={formData.freeMonths}
                onChange={(e) => handleInputChange('freeMonths', parseInt(e.target.value) || 0)}
                className={errors.freeMonths ? 'border-red-500' : ''}
                disabled={creating}
              />
              {errors.freeMonths && (
                <p className="text-red-500 text-xs mt-1">{errors.freeMonths}</p>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900 font-medium">{getSummaryText()}</p>
            {formData.planType !== 'none' && (
              <p className="text-xs text-amber-700 mt-1">
                {formData.planType === 'trial' && `Trial expires in ${formData.trialDays} days`}
                {formData.planType === 'subscription' && `Subscription active for ${formData.subscriptionMonths} month${formData.subscriptionMonths > 1 ? 's' : ''}`}
                {formData.planType === 'freeMonths' && `${formData.freeMonths} free month${formData.freeMonths > 1 ? 's' : ''} granted`}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Creates a nav_users record
            </span>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creating}
              variant="amber"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
